import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FieldError, Input, Label, TextField } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { VariantBarcodesEditor } from './useVariantBarcodesForm';

type VariantBarcodesAddRowProps = {
    editor: VariantBarcodesEditor;
};

/**
 * "Add a barcode" with **Add**: Enter adds too, as a scanner sends it, and
 * Escape still reaches the drawer and its discard question.
 */
export const VariantBarcodesAddRow: React.FC<VariantBarcodesAddRowProps> = ({
    editor,
}) => {
    const { t } = useTranslation('pages');
    const { draft, errors, isSaving, inputRefOf, onAddTextChange, onAdd } =
        editor;

    return (
        <div className="flex flex-wrap items-start gap-2">
            <TextField
                className="flex min-w-0 flex-1 basis-48 flex-col gap-1"
                isDisabled={isSaving}
                isInvalid={errors.add !== undefined}
                onChange={onAddTextChange}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        onAdd();
                        return;
                    }
                    // React Aria stops a handled key here; Escape must still
                    // reach the drawer and its discard question.
                    event.continuePropagation();
                }}
                value={draft.addText}
                {...getNodeTestIdProp('variant-barcodes-add-field')}
            >
                <Label>{t('products.detail.barcodes.addLabel')}</Label>
                <Input
                    placeholder={t('products.detail.barcodes.addPlaceholder')}
                    ref={inputRefOf('add')}
                />
                <FieldError>
                    {errors.add === undefined
                        ? null
                        : t(errors.add.key, errors.add.values)}
                </FieldError>
            </TextField>
            {/* Level with the input, under the field's label. */}
            <Button
                className="mt-6"
                isDisabled={isSaving || draft.addText.trim().length === 0}
                onPress={onAdd}
                variant="secondary"
                {...getNodeTestIdProp('variant-barcodes-add')}
            >
                {t('products.detail.barcodes.add')}
            </Button>
        </div>
    );
};
